import { createFeature, createReducer, on } from '@ngrx/store';

import { ServiceListItemDto, ServiceReadDto } from '../models/service-management-api';
import { ServiceManagementActions } from './service-management.actions';
import { initialServiceStats, initialServiceManagementState } from './service-management.state';

export const serviceManagementFeature = createFeature({
  name: 'serviceManagement',
  reducer: createReducer(
    initialServiceManagementState,
    on(ServiceManagementActions.loadList, (state, { query }) => ({
      ...state,
      loadingList: true,
      error: null,
      currentPage: query.page,
      pageSize: query.pageSize,
    })),
    on(
      ServiceManagementActions.loadListSuccess,
      (state, { rows, total, page, pageSize, stats }) => ({
        ...state,
        rows,
        totalServices: total || stats.allServices,
        activeServices: stats.activeServices,
        inactiveServices: stats.inactiveServices,
        currentPage: page,
        pageSize,
        loadingList: false,
      }),
    ),
    on(ServiceManagementActions.loadListFailure, (state, { error }) => ({
      ...state,
      rows: [],
      totalServices: initialServiceStats.allServices,
      activeServices: initialServiceStats.activeServices,
      inactiveServices: initialServiceStats.inactiveServices,
      loadingList: false,
      error,
    })),
    on(ServiceManagementActions.loadService, (state, { serviceId, mode }) => ({
      ...state,
      loadingDetail: true,
      error: null,
      selectedServiceId: serviceId,
      selectedMode: mode,
    })),
    on(ServiceManagementActions.loadServiceSuccess, (state, { service, mode }) => ({
      ...state,
      loadingDetail: false,
      selectedService: service,
      selectedServiceId: service.serviceId,
      selectedMode: mode,
    })),
    on(ServiceManagementActions.loadServiceFailure, (state, { error }) => ({
      ...state,
      loadingDetail: false,
      error,
    })),
    on(ServiceManagementActions.createService, ServiceManagementActions.updateService, (state) => ({
      ...state,
      submitting: true,
      error: null,
      successMessage: null,
    })),
    on(ServiceManagementActions.persistSuccess, (state, { message, service }) => ({
      ...state,
      submitting: false,
      successMessage: message,
      selectedService: service,
      selectedServiceId: service.serviceId,
      rows: upsertServiceListItem(state.rows, service),
    })),
    on(ServiceManagementActions.persistFailure, (state, { error }) => ({
      ...state,
      submitting: false,
      error,
    })),
    on(ServiceManagementActions.updateServiceStatus, (state, { serviceId }) => ({
      ...state,
      statusUpdatingId: serviceId,
      error: null,
      successMessage: null,
    })),
    on(ServiceManagementActions.updateServiceStatusSuccess, (state, { message, service }) => ({
      ...state,
      statusUpdatingId: null,
      successMessage: message,
      selectedService:
        state.selectedServiceId === service.serviceId ? service : state.selectedService,
      rows: upsertServiceListItem(state.rows, service),
    })),
    on(ServiceManagementActions.updateServiceStatusFailure, (state, { error }) => ({
      ...state,
      statusUpdatingId: null,
      error,
    })),
    on(ServiceManagementActions.updateServiceSla, (state, { serviceId }) => ({
      ...state,
      slaUpdatingId: serviceId,
      error: null,
      successMessage: null,
    })),
    on(ServiceManagementActions.updateServiceSlaSuccess, (state, { message, service }) => ({
      ...state,
      slaUpdatingId: null,
      successMessage: message,
      selectedService:
        state.selectedServiceId === service.serviceId ? service : state.selectedService,
      rows: upsertServiceListItem(state.rows, service),
    })),
    on(ServiceManagementActions.updateServiceSlaFailure, (state, { error }) => ({
      ...state,
      slaUpdatingId: null,
      error,
    })),
    on(ServiceManagementActions.exportServices, (state) => ({
      ...state,
      exporting: true,
      error: null,
    })),
    on(ServiceManagementActions.exportServicesSuccess, (state) => ({
      ...state,
      exporting: false,
    })),
    on(ServiceManagementActions.exportServicesFailure, (state, { error }) => ({
      ...state,
      exporting: false,
      error,
    })),
    on(ServiceManagementActions.clearError, (state) => ({
      ...state,
      error: null,
    })),
    on(ServiceManagementActions.clearSuccessMessage, (state) => ({
      ...state,
      successMessage: null,
    })),
  ),
});

export const {
  name: serviceManagementFeatureKey,
  reducer: serviceManagementReducer,
  selectRows,
  selectTotalServices,
  selectActiveServices,
  selectInactiveServices,
  selectLoadingList,
  selectLoadingDetail,
  selectSubmitting,
  selectExporting,
  selectStatusUpdatingId,
  selectSlaUpdatingId,
  selectError,
  selectSuccessMessage,
  selectSelectedService,
  selectSelectedServiceId,
  selectSelectedMode,
  selectCurrentPage,
  selectPageSize,
} = serviceManagementFeature;

function upsertServiceListItem(
  rows: ServiceListItemDto[],
  service: ServiceReadDto,
): ServiceListItemDto[] {
  const nextRow: ServiceListItemDto = {
    serviceId: service.serviceId,
    serviceName: service.serviceName,
    slaTimeHours: service.slaTimeHours,
    reworkAllowed: service.reworkAllowed,
    reworkSlaTimeHours: service.reworkSlaTimeHours,
    status: service.status,
    image: service.image,
    createdAt: service.createdAt,
  };
  const existingIndex = rows.findIndex((row) => row.serviceId === service.serviceId);

  if (existingIndex === -1) {
    return [nextRow, ...rows];
  }

  return rows.map((row) => (row.serviceId === service.serviceId ? nextRow : row));
}
